<template>
  <property-report-card
    title="Unit Type Metrics"
    description="See stats broken down by unit type across your comps."
    transparent-card
    :hide-header="!printingMode"
    :printing-mode="printingMode"
  >
    <div class="row q-col-gutter-sm" ref="container">
      <div
        v-for="(bed, index) in unitTypeMetric"
        :key="bed.bed"
        class="col"
        :class="{
          'col-3':
            unitTypeMetric.length < 3 || (unitTypeMetric.length >= 5 && containerWidth < 1920),
          'col-6': containerWidth < 1025,
          'col-12': containerWidth < 600
        }"
      >
        <q-card :id="`intercom-${index}-unit-type-metric`" flat bordered>
          <q-card-section
            class="row justify-between items-center"
            :style="{
              borderBottom: `4px solid ${unitTypeColor(bed.bed)}`
            }"
          >
            <div class="text-h6">{{ unitTypeName(bed.bed) }}</div>
            <div class="trailing-text">{{ trailingText }}</div>
          </q-card-section>
          <q-card-section class="row q-col-gutter-x-md q-col-gutter-y-xs">
            <div
              v-for="ins in bed.insights"
              :key="ins.label"
              class="text-caption col-12 justify-between flex"
              :class="{ 'col-6': containerWidth < 1024 }"
            >
              <span class="text-grey-7">{{ ins.label }} </span>

              <span class="highlighted-number">{{ ins.valueFormatted }}</span>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </property-report-card>
</template>

<script lang="ts">
import {
  type Availability,
  type PropertyDetailsResponse,
  type Price,
  type AvailabilityPeriod,
  avgPriceAndPSFAgg
} from 'common'
import { daysOnMarket } from 'common'
import type { PropType } from 'vue'
import { formatMoney, formatNumber, avg, sum, unitTypeColor } from '@/utils'
import { differenceInDays, format } from 'date-fns'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

type Insight = {
  bed: number
  insights: {
    label: string
    description: string
    valueFormatted: string
  }[]
}

export default {
  name: 'PropertyCompareUnitTypeMetrics',
  components: { PropertyReportCard },
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      containerWidth: 0,
      observer: null as ResizeObserver | null,
      unitTypeColor,
      formatMoney,
      formatNumber,
      metrics: [
        {
          label: '# Listings',
          description: 'Number of units of this type at your property',
          getter: () => [1],
          agg: sum,
          formatter: (val: number | null) => (val ? formatNumber(val) : '−')
        },
        {
          label: 'Days on Market',
          description: 'Average number of days a unit is on the market before being leased',
          getter: ({ period }) => [daysOnMarket(period, this.dateRange.to)],
          agg: avg,
          formatter: (val: number | null) => (val ? formatNumber(val) : '−')
        },
        {
          label: 'Avg Sqft',
          description: 'Average square feet of this type at your property',
          getter: ({ unit }) => [(unit.sqft || unit.min_sqft) ?? null],
          agg: avg,
          formatter: (val: number | null) => (val ? formatNumber(val) + ' ft²' : '−')
        },
        {
          label: 'Asking Rent / PSF',
          description: 'Average rent for a unit, before applying detected concessions',
          getter: ({ unit, lastHistory }) => [
            {
              price: lastHistory.price ?? lastHistory.min_price,
              sqft: unit.sqft || unit.min_sqft
            }
          ],
          agg: avgPriceAndPSFAgg,
          formatter: (val: { avg_price: number | null; avg_psf: number | null }) => {
            if (!val || (val.avg_price == null && val.avg_psf == null)) return '−'
            const rentStr = val.avg_price ? formatMoney(val.avg_price, 0) : '−'
            const psfStr = val.avg_psf ? formatMoney(val.avg_psf, 2) : '−'
            return `${rentStr} / ${psfStr}`
          }
        },
        {
          label: 'Effective Rent / PSF',
          description:
            'Average effective rent per square foot for a unit, after applying detected concessions',
          getter: ({ unit, lastHistory }) => [
            {
              price: lastHistory.effective_price || lastHistory.min_effective_price,
              sqft: unit.sqft || unit.min_sqft
            }
          ],
          agg: avgPriceAndPSFAgg,
          formatter: (val: { avg_price: number | null; avg_psf: number | null }) => {
            if (!val || (val.avg_price == null && val.avg_psf == null)) return '−'
            const rentStr = val.avg_price ? formatMoney(val.avg_price, 0) : '−'
            const psfStr = val.avg_psf ? formatMoney(val.avg_psf, 2) : '−'
            return `${rentStr} / ${psfStr}`
          }
        }
      ] as {
        label: string
        description: string
        getter: (val: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }) => any[]
        agg: (vals: any[]) => any
        formatter: (val: any) => string
      }[]
    }
  },
  mounted() {
    this.observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.containerWidth = entry.contentRect.width
      }
    })
    this.observer.observe(this.$refs.container as Element)
  },
  beforeUnmount() {
    if (this.observer && this.$refs.container) {
      this.observer.unobserve(this.$refs.container as Element)
      this.observer.disconnect()
    }
  },
  computed: {
    trailingText() {
      if (this.dateRange.to.toDateString() !== new Date().toDateString()) {
        return `${format(this.dateRange.from, 'M/d/yyyy')} - ${format(this.dateRange.to, 'M/d/yyyy')}`
      }
      const days = differenceInDays(this.dateRange.to, this.dateRange.from)
      if (days < 7) {
        return `Trailing ${days} day${days > 1 ? 's' : ''}`
      }
      if (days % 30 === 0) {
        const months = days / 30
        return `Trailing ${months} month${months > 1 ? 's' : ''}`
      }
      if (days % 7 === 0) {
        const weeks = days / 7
        return `Trailing ${weeks} week${weeks > 1 ? 's' : ''}`
      }
      return `Trailing ${days} day${days > 1 ? 's' : ''}`
    },
    unitTypeMetric(): Insight[] {
      const beds = [0, 1, 2, 3, 4, 5, 6]
      const metricValues: Record<number, Record<string, { values: (null | number)[] }>> = {}

      // instanciate all of them
      for (const bed of beds) {
        metricValues[bed] = {}
        for (const metric of this.metrics) {
          metricValues[bed][metric.label] = { values: [] }
        }
      }

      for (const { units } of this.filteredProperties) {
        for (const { unit, lastHistory, period } of units) {
          const bed = unit.bed
          for (const metric of this.metrics) {
            if (bed !== null && beds.includes(bed)) {
              const val = metric.getter({ unit, lastHistory, period })
              metricValues[bed][metric.label].values.push(...val)
            }
          }
        }
      }

      return beds
        .filter((bed) => metricValues[bed]['# Listings'].values.length > 0)
        .map((bed) => {
          return {
            bed,
            insights: this.metrics.map((metric) => {
              const values = metricValues[bed][metric.label].values
              return {
                label: metric.label,
                description: metric.description,
                valueFormatted: metric.formatter(metric.agg(values))
              }
            })
          }
        })
    }
  },
  methods: {
    unitTypeName(bed: number): string {
      return bed === 0 ? 'Studio' : `${bed} BRs`
    }
  }
}
</script>

<style scoped>
.trailing-text {
  font-size: 1em;
  color: #999;
  font-weight: 500;
}

.highlighted-number {
  font-weight: 600;
}
</style>
